import { Controller } from "@hotwired/stimulus";
import { EditorState } from "@codemirror/state";
import { EditorView } from "@codemirror/view";
import { basicSetup } from "codemirror";
import { javascript } from "@codemirror/lang-javascript";
import { json } from "@codemirror/lang-json";
import debounce from "lodash/debounce";

export default class extends Controller {
  static targets = ["expression", "input", "output", "errorMessage"];

  static values = {
    evaluationUrl: String,
  };

  connect() {
    this.initializeEditors();
    this.debouncedEvaluate = debounce(this.evaluate.bind(this), 500);
  }

  initializeEditors() {
    // Create containers for the editors
    const expressionContainer = document.createElement("div");
    this.expressionTarget.parentNode.replaceChild(
      expressionContainer,
      this.expressionTarget
    );

    const inputContainer = document.createElement("div");
    this.inputTarget.parentNode.replaceChild(inputContainer, this.inputTarget);

    // Default content
    const defaultExpression = "a + b";
    const defaultInput = '{\n  "a": 10,\n  "b": 5\n}';

    // FEEL expression editor
    this.expressionState = EditorState.create({
      doc: defaultExpression,
      extensions: [
        basicSetup,
        javascript(),
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            this.debouncedEvaluate(); // Use debounced version
          }
        }),
      ],
    });

    this.expressionEditor = new EditorView({
      state: this.expressionState,
      parent: expressionContainer,
    });

    // Variables data editor (JSON)
    this.inputState = EditorState.create({
      doc: defaultInput,
      extensions: [
        basicSetup,
        json(),
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            this.debouncedEvaluate(); // Use debounced version
          }
        }),
      ],
    });

    this.inputEditor = new EditorView({
      state: this.inputState,
      parent: inputContainer,
    });
  }

  evaluate() {
    const expression = this.expressionEditor.state.doc.toString();
    let input;

    try {
      input = JSON.parse(this.inputEditor.state.doc.toString());
      this.errorMessageTarget.textContent = "";
    } catch (e) {
      this.errorMessageTarget.textContent = `Input data error: ${e.message}`;
      return;
    }

    fetch(this.evaluationUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
          .content,
      },
      body: JSON.stringify({
        expression: expression,
        input: input,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          this.errorMessageTarget.textContent = data.error;
          this.outputTarget.textContent = "";
        } else {
          this.errorMessageTarget.textContent = "";
          this.outputTarget.textContent = JSON.stringify(data.output, null, 2);
        }
      })
      .catch((error) => {
        this.errorMessageTarget.textContent = `Evaluation error: ${error.message}`;
      });
  }

  saveExpression() {
    const expression = this.expressionEditor.state.doc.toString();
    const input = this.inputEditor.state.doc.toString();

    localStorage.setItem("feelSandbox.expression", expression);
    localStorage.setItem("feelSandbox.input", input);

    this.showToast("Expression saved!");
  }

  loadExpression() {
    const expression = localStorage.getItem("feelSandbox.expression");
    const input = localStorage.getItem("feelSandbox.input");

    if (expression) {
      this.expressionEditor.dispatch({
        changes: {
          from: 0,
          to: this.expressionEditor.state.doc.length,
          insert: expression,
        },
      });
    }

    if (input) {
      this.inputEditor.dispatch({
        changes: {
          from: 0,
          to: this.inputEditor.state.doc.length,
          insert: input,
        },
      });
    }

    this.showToast("Expression loaded!");
  }

  showToast(message) {
    const toast = document.createElement("div");
    toast.className =
      "fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50";
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.remove();
    }, 3000);
  }
}
